// src/components/feeTerms/FeeTermForm.js
import React, { useState, useEffect } from 'react';
import { format, parseISO, parse } from 'date-fns';

// Constants
const TERM_NAME_MAX_LENGTH = 100;

// Helper to format date for date input (YYYY-MM-DD)
const formatDateForDateInput = (date) => {
    if (!date) return '';
    try {
        // Handles both Date objects and ISO strings
        const d = date instanceof Date ? date : parseISO(date);
        return format(d, "yyyy-MM-dd");
    } catch (e) {
        console.warn("Failed to format date for input:", date, e);
        return '';
    }
};

// Helper to safely parse number input, allowing empty string or valid number
const parseOptionalNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const number = parseFloat(value);
    return isNaN(number) ? null : number; // Return null if parsing fails
}

// Default structure, especially for create mode
const defaultFeeTermData = (schoolId) => ({
    schoolId: schoolId || '',
    termName: '',
    dueDate: formatDateForDateInput(new Date()), // Default to today
    percentageDue: null, // Start as null
    fixedAmount: null,   // Start as null
    lateFeeApplicable: false,
    displayOrder: 0,
    isActive: true,
    amountType: 'none', // 'percentage', 'fixed', or 'none' - UI state helper
});

function FeeTermForm({ initialData, mode, onSave, onCancel, isLoading, apiError, currentSchoolId }) {
    const isEditMode = mode === 'edit';

    // Initialize form state
    const [formData, setFormData] = useState(() => {
        const schoolIdToUse = initialData?.schoolId || currentSchoolId || '';
        if (isEditMode && initialData) {
            const hasPercentage = initialData.percentageDue !== null && initialData.percentageDue !== undefined;
            const hasFixed = initialData.fixedAmount !== null && initialData.fixedAmount !== undefined;
            let amountType = 'none';
            if (hasPercentage) amountType = 'percentage';
            else if (hasFixed) amountType = 'fixed';

            return {
                schoolId: schoolIdToUse, // Use fetched ID
                termName: initialData.termName || '',
                dueDate: formatDateForDateInput(initialData.dueDate),
                percentageDue: initialData.percentageDue, // Store raw number
                fixedAmount: initialData.fixedAmount,     // Store raw number
                lateFeeApplicable: initialData.lateFeeApplicable ?? false,
                displayOrder: initialData.displayOrder ?? 0,
                isActive: initialData.isActive ?? true,
                amountType: amountType, // Set based on data
            };
        } else {
            // Create mode: use defaults with the current schoolId
            return defaultFeeTermData(schoolIdToUse);
        }
    });

    const [formError, setFormError] = useState(null); // Local validation errors

    // Check for missing schoolId (critical config)
    useEffect(() => {
        if (!formData.schoolId) {
            console.error("Critical: School ID missing in form data.");
            setFormError("Configuration error: School ID is missing. Cannot save.");
        } else {
            // Clear specific error if schoolId becomes available
            if (formError === "Configuration error: School ID is missing. Cannot save.") {
                setFormError(null);
            }
        }
    }, [formData.schoolId, formError]);

    // Handle general input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormError(null); // Clear validation errors on change

        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else if (name === 'amountType') {
            // Reset the *other* amount type when switching
            const isPercentage = value === 'percentage';
            setFormData(prev => ({
                ...prev,
                amountType: value,
                percentageDue: isPercentage ? prev.percentageDue : null, // Keep current if switching to it, else null
                fixedAmount: !isPercentage ? prev.fixedAmount : null, // Keep current if switching to it, else null
            }));
        } else if (name === 'percentageDue' || name === 'fixedAmount') {
             // Allow empty string for clearing the input, parse to null for state
             const numericValue = parseOptionalNumber(value);
              setFormData(prev => ({ ...prev, [name]: numericValue }));
        } else if (name === 'termName' && value.length > TERM_NAME_MAX_LENGTH) {
            // Prevent exceeding max length for term name
            return;
        }
        else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        setFormError(null);

        // --- Client-side Validation ---
        if (!formData.schoolId) {
            setFormError("School ID is missing."); return;
        }
      
        if (!formData.termName?.trim()) {
            setFormError("Term Name is required."); return;
        }
        if (!formData.dueDate) {
            setFormError("Due Date is required."); return;
        }
        // Validate date is present or future (basic check)
        try {
             const today = new Date();
             today.setHours(0, 0, 0, 0); // Set to start of day for comparison
             // Parse YYYY-MM-DD string into a Date object
             const dueDateObj = parse(formData.dueDate, 'yyyy-MM-dd', new Date());
             dueDateObj.setHours(0, 0, 0, 0); // Also set to start of day
            if (dueDateObj < today) {
                 setFormError("Due date cannot be in the past."); return;
            }
        } catch (dateError) {
             setFormError("Invalid Due Date format."); return;
        }

        // Validate amounts based on selection
        const percentageValue = formData.percentageDue;
        const fixedValue = formData.fixedAmount;

        if (formData.amountType === 'percentage' && (percentageValue === null || percentageValue < 0)) {
             setFormError("Percentage Due is required and cannot be negative."); return;
        }
        if (formData.amountType === 'fixed' && (fixedValue === null || fixedValue < 0)) {
            setFormError("Fixed Amount is required and cannot be negative."); return;
        }
         if (formData.amountType === 'none') {
            setFormError("Please select and enter either Percentage Due or Fixed Amount."); return;
        }

        const displayOrderValue = parseInt(formData.displayOrder, 10);
        if (isNaN(displayOrderValue)) {
            setFormError("Display Order must be a valid number."); return;
        }
        // --- End Validation ---

        // Prepare data payload based on Create or Update DTO
        let dataToSend = {};
        if (isEditMode) {
            dataToSend = {
                // Update DTO fields (all optional)
                termName: formData.termName.trim(),
                dueDate: formData.dueDate, // Send as YYYY-MM-DD string, backend parses
                percentageDue: formData.amountType === 'percentage' ? percentageValue : null,
                fixedAmount: formData.amountType === 'fixed' ? fixedValue : null,
                lateFeeApplicable: formData.lateFeeApplicable,
                displayOrder: displayOrderValue,
                isActive: formData.isActive,
            };
             // Filter out null values for optional fields if backend expects non-null only for updates
             Object.keys(dataToSend).forEach(key => {
                if (dataToSend[key] === null || dataToSend[key] === undefined) {
                    // Decide whether to delete or send null based on backend PUT behavior (PATCH vs PUT)
                    // Assuming PUT replaces, we might want to send nulls explicitly
                    // Or if PATCH, filter them out: delete dataToSend[key];
                }
            });

        } else {
            // Create DTO fields (some required)
            dataToSend = {
                schoolId: formData.schoolId,
                termName: formData.termName.trim(),
                dueDate: formData.dueDate, // Send as YYYY-MM-DD
                percentageDue: formData.amountType === 'percentage' ? percentageValue : null,
                fixedAmount: formData.amountType === 'fixed' ? fixedValue : null,
                lateFeeApplicable: formData.lateFeeApplicable,
                displayOrder: displayOrderValue,
                isActive: formData.isActive,
            };
        }

        console.log("Data to send:", dataToSend);
        onSave(dataToSend); // Pass validated and formatted data to parent handler
    };

    // Render the form
    return (
        <div>
            {/* Display API errors from parent OR local validation errors */}
            {(apiError || formError) && <p style={styles.errorText}>{apiError || formError}</p>}

            <form onSubmit={handleSubmit} style={{ margin: 0, padding: 0 }}>
                <div style={styles.formGrid}> {/* Use Grid for better alignment */}

                    {/* Term Name */}
                    <div style={styles.formGroup}>
                        <label htmlFor="termName" style={styles.label}>Term Name*</label>
                        <input
                            type="text" id="termName" name="termName"
                            value={formData.termName}
                            onChange={handleInputChange}
                            required maxLength={TERM_NAME_MAX_LENGTH}
                            style={styles.input} disabled={isLoading}
                            placeholder="e.g., First Term Fees"
                        />
                         <div style={styles.charCount}>
                            {formData.termName.length}/{TERM_NAME_MAX_LENGTH}
                         </div>
                    </div>

                

                    {/* Due Date */}
                    <div style={styles.formGroup}>
                        <label htmlFor="dueDate" style={styles.label}>Due Date*</label>
                        <input
                            type="date" id="dueDate" name="dueDate"
                            value={formData.dueDate}
                            onChange={handleInputChange}
                            required style={styles.input} disabled={isLoading}
                        />
                    </div>

                     {/* Amount Type Selection */}
                     <div style={{...styles.formGroup, gridColumn: '1 / -1'}}> {/* Span full width */}
                         <label style={styles.label}>Amount Due Type*</label>
                         <div style={styles.radioGroup}>
                             <label style={styles.radioLabel}>
                                <input type="radio" name="amountType" value="percentage"
                                       checked={formData.amountType === 'percentage'}
                                       onChange={handleInputChange} disabled={isLoading} />
                                Percentage (%)
                             </label>
                             <label style={styles.radioLabel}>
                                <input type="radio" name="amountType" value="fixed"
                                       checked={formData.amountType === 'fixed'}
                                       onChange={handleInputChange} disabled={isLoading} />
                                Fixed Amount ($) {/* Adjust symbol if needed */}
                            </label>
                         </div>
                     </div>

                     {/* Percentage Input (Conditional) */}
                     {formData.amountType === 'percentage' && (
                        <div style={styles.formGroup}>
                            <label htmlFor="percentageDue" style={styles.label}>Percentage Due*</label>
                            <input
                                type="number" id="percentageDue" name="percentageDue"
                                value={formData.percentageDue ?? ''} // Use empty string for null/undefined display
                                onChange={handleInputChange}
                                required min="0" step="0.01" // Allow decimals
                                style={styles.input} disabled={isLoading}
                                placeholder="e.g., 50.00"
                            />
                        </div>
                     )}

                     {/* Fixed Amount Input (Conditional) */}
                     {formData.amountType === 'fixed' && (
                        <div style={styles.formGroup}>
                            <label htmlFor="fixedAmount" style={styles.label}>Fixed Amount*</label>
                            <input
                                type="number" id="fixedAmount" name="fixedAmount"
                                value={formData.fixedAmount ?? ''} // Use empty string for null/undefined display
                                onChange={handleInputChange}
                                required min="0" step="0.01" // Allow decimals
                                style={styles.input} disabled={isLoading}
                                placeholder="e.g., 1000.00"
                            />
                        </div>
                    )}

                     {/* Display Order */}
                     <div style={styles.formGroup}>
                        <label htmlFor="displayOrder" style={styles.label}>Display Order</label>
                        <input
                            type="number" id="displayOrder" name="displayOrder"
                            value={formData.displayOrder}
                            onChange={handleInputChange}
                            min="0" step="1"
                            style={{...styles.input, ...styles.inputSmall}} disabled={isLoading}
                        />
                    </div>

                    {/* Late Fee Applicable */}
                    <div style={{...styles.formGroup, ...styles.checkboxAlign}}>
                        <label htmlFor="lateFeeApplicable" style={{...styles.label, marginBottom: 0, marginRight: '10px'}}>Late Fee Applicable:</label>
                        <input
                            type="checkbox" id="lateFeeApplicable" name="lateFeeApplicable"
                            checked={formData.lateFeeApplicable}
                            onChange={handleInputChange}
                            style={styles.checkbox} disabled={isLoading}
                        />
                    </div>

                    {/* Is Active */}
                     <div style={{...styles.formGroup, ...styles.checkboxAlign}}>
                        <label htmlFor="isActive" style={{...styles.label, marginBottom: 0, marginRight: '10px'}}>Active:</label>
                        <input
                            type="checkbox" id="isActive" name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                            style={styles.checkbox} disabled={isLoading}
                        />
                    </div>

                </div> {/* End Form Grid */}

                {/* Action Buttons */}
                <div style={styles.buttonGroup}>
                    <button
                        type="submit"
                        style={isLoading || !formData.schoolId ? { ...styles.buttonPrimary, ...styles.buttonDisabled } : styles.buttonPrimary}
                        disabled={isLoading || !formData.schoolId} // Disable if loading or no schoolId
                    >
                        {isLoading ? <span style={styles.spinner}></span> : (isEditMode ? 'Update Term' : 'Create Term')}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={styles.buttonSecondary}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}

// --- Styles --- (Similar to BannerForm, adjusted for grid)
const styles = {
    errorText: {
        color: '#e74c3c',
        backgroundColor: '#fdeded',
        border: '1px solid #f5c6cb',
        padding: '10px 15px',
        borderRadius: '4px',
        marginBottom: '20px',
        textAlign: 'center',
        fontSize: '0.9em',
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', // Responsive columns
        gap: '15px 25px', // Row and column gap
        alignItems: 'start', // Align items to the start of their grid cell
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column', // Stack label and input vertically
    },
    label: {
        display: 'block',
        marginBottom: '6px',
        fontWeight: '600',
        color: '#495057',
        fontSize: '0.9em',
    },
    input: {
        width: '100%',
        padding: '10px 12px',
        border: '1px solid #ced4da',
        borderRadius: '4px',
        boxSizing: 'border-box',
        fontSize: '1em',
        transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
        ':disabled': {
            backgroundColor: '#e9ecef',
            cursor: 'not-allowed',
        },
    },
    inputSmall: { // For numerical inputs like Order, Year ID
        maxWidth: '150px',
    },
     charCount: {
        textAlign: 'right',
        fontSize: '0.75em',
        color: '#6c757d',
        marginTop: '3px',
        height: '1em', // Reserve space even when empty
    },
     radioGroup: {
        display: 'flex',
        gap: '20px',
        marginTop: '5px',
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '0.95em',
        fontWeight: 'normal',
        cursor: 'pointer',
    },
    checkbox: {
        width: '17px', // Slightly smaller checkbox
        height: '17px',
        cursor: 'pointer',
        accentColor: '#007bff', // Color the checkmark blue
        ':disabled': {
            cursor: 'not-allowed',
            accentColor: '#adb5bd',
        }
    },
    checkboxAlign: {
        flexDirection: 'row', // Align label and checkbox horizontally
        alignItems: 'center',
        paddingTop: '25px', // Align vertically with other inputs' labels approx
         minHeight: '60px', // Ensure consistent height with other form groups
    },
    buttonGroup: {
        marginTop: '30px',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '15px',
        borderTop: '1px solid #e9ecef',
        paddingTop: '25px',
    },
    buttonPrimary: {
        padding: '10px 25px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '1em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s ease',
    },
    buttonSecondary: {
        padding: '10px 20px',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '1em',
        transition: 'background-color 0.2s ease',
    },
    buttonDisabled: {
        backgroundColor: '#adb5bd',
        cursor: 'not-allowed',
        opacity: 0.7,
    },
    spinner: {
        display: 'inline-block',
        width: '1em',
        height: '1em',
        border: '3px solid rgba(255,255,255,.3)',
        borderRadius: '50%',
        borderTopColor: '#fff',
        animation: 'spin 1s linear infinite',
    },
    '@keyframes spin': {
         to: { transform: 'rotate(360deg)' }
    }
};

export default FeeTermForm;